import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CreditCard, CalendarClock, DoorOpen, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

const ORANGE = '#FF5E00';
const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ label, amount, credit, testid }) => (
  <div className="flex justify-between py-2 border-b border-white/10 text-sm" data-testid={testid}>
    <span className={credit ? 'text-emerald-300' : 'text-gray-200'}>{label}</span>
    <span className={`whitespace-nowrap pl-3 ${credit ? 'text-emerald-300' : 'text-white font-bold'}`}>{credit ? `− ${usd(amount)}` : usd(amount)}</span>
  </div>
);

const Option = ({ icon: Icon, title, children, testid }) => (
  <div className="border-2 border-white/15 p-5 transition-colors duration-200 hover:border-orange-500/60" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2"><Icon className="w-5 h-5" style={{ color: ORANGE }} /><h3 className="font-black uppercase tracking-tight">{title}</h3></div>
    {children}
  </div>
);

const Done = ({ b }) => (
  <div className="border-2 border-emerald-500/50 bg-emerald-500/10 p-6 mt-6" data-testid="balance-done">
    <CheckCircle2 className="w-6 h-6 text-emerald-400 mb-2" />
    {b.status === 'withdrawn'
      ? <p className="text-sm">Withdrawal received. {b.student_name}'s account is now closed. We're grateful for the time we had together.</p>
      : <p className="text-sm">Thank you — your payment is recorded. {b.owed > 0 ? `Remaining balance: ${usd(b.owed)}. You can come back to this page any time to pay more.` : 'Your balance is paid in full.'}</p>}
  </div>
);

export default function TuitionBalance() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [amt, setAmt] = useState('');
  const [confirmW, setConfirmW] = useState(false);
  const [justPaid, setJustPaid] = useState(false);

  useEffect(() => {
    const sid = params.get('session_id');
    const req = sid ? api.get(`/balance/${token}/status/${sid}`) : api.get(`/balance/${token}`);
    req.then((r) => { setB(r.data.balance); if (r.data.paid) setJustPaid(true); })
      .catch(() => setErr('We could not find this balance. Please email info@prsvr.com.'));
  }, [token, params]);

  const pay = async (amount) => {
    setErr('');
    const min = !b.payments.length && b.min_first_payment ? Math.min(b.min_first_payment, b.owed) : 1;
    if (!(amount >= min)) return setErr(`Please enter an amount of at least ${usd(min)}.`);
    setBusy('pay');
    try {
      const r = await api.post(`/balance/${token}/checkout`, { amount, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { setErr(e?.response?.data?.detail || 'Payment could not be started.'); setBusy(''); }
  };

  const withdraw = async () => {
    setBusy('withdraw');
    try { const r = await api.post(`/balance/${token}/withdraw`); setB(r.data.balance); }
    catch { setErr('Could not record the withdrawal. Please email info@prsvr.com.'); }
    finally { setBusy(''); }
  };

  if (!b) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="balance-loading">
      {err ? <p className="text-sm text-red-300" data-testid="balance-error">{err}</p> : <Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} />}
    </div>
  );

  const open = !['withdrawn', 'closed', 'paid'].includes(b.status);
  return (
    <div className="min-h-screen bg-black text-white px-5 py-12" data-testid="balance-page">
      <div className="max-w-2xl mx-auto">
        <p className="text-[10px] font-black tracking-[0.3em] uppercase" style={{ color: ORANGE }}>Scholar Athlete Academy · Tuition</p>
        <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight mt-2">{b.student_name}</h1>
        <p className="text-sm text-gray-400 mt-2">{b.family} · Respond by <b className="text-white">{b.deadline_label}</b></p>

        <div className="mt-8 border-2 border-white/15 p-5" data-testid="balance-breakdown">
          {b.items.map((i, k) => <Row key={k} label={i.label} amount={i.amount} testid={`balance-item-${k}`} />)}
          {b.credits.map((c, k) => <Row key={k} label={c.label} amount={c.amount} credit testid={`balance-credit-${k}`} />)}
          {b.payments.map((p, k) => <Row key={k} label={`Payment received · ${p.paid_at.slice(0, 10)}`} amount={p.amount} credit testid={`balance-payment-${k}`} />)}
          <div className="flex justify-between pt-3 text-lg font-black"><span>Balance owed</span><span style={{ color: ORANGE }} data-testid="balance-owed">{usd(b.owed)}</span></div>
        </div>

        {(justPaid || !open) && <Done b={b} />}
        {err && <p className="text-sm text-red-300 mt-4" data-testid="balance-error">{err}</p>}

        {open && (
          <div className="grid gap-4 mt-8">
            <Option icon={CreditCard} title="1 · Pay in full" testid="balance-option-full">
              <p className="text-sm text-gray-300 mb-3">Settle the full balance today and {b.student_name.split(' ')[0]} keeps uninterrupted access.</p>
              <button onClick={() => pay(b.owed)} disabled={!!busy} className="px-5 py-3 font-black uppercase text-black rounded-full transition-transform duration-200 hover:scale-[1.03]" style={{ backgroundColor: ORANGE }} data-testid="balance-pay-full-btn">
                {busy === 'pay' ? 'Opening…' : `Pay ${usd(b.owed)}`}
              </button>
            </Option>
            <Option icon={CalendarClock} title="2 · Start a payment plan" testid="balance-option-plan">
              <p className="text-sm text-gray-300 mb-3" data-testid="balance-plan-text">
                {!b.payments.length && b.min_first_payment
                  ? `Start with an initial payment of at least ${usd(b.min_first_payment)} to keep access uninterrupted. After that, come back to this same page to pay more in any amount until the balance is cleared.`
                  : 'Pay any amount toward the balance. Come back to this same page to pay more until the balance is cleared.'}
              </p>
              <div className="flex gap-2">
                <div className="flex items-center border-2 border-white/20 px-3"><span className="text-gray-400">$</span>
                  <input type="number" min="1" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Amount" className="bg-transparent text-white outline-none py-2 px-1 w-28" style={{ backgroundColor: 'transparent', color: '#fff' }} data-testid="balance-plan-amount-input" />
                </div>
                <button onClick={() => pay(parseFloat(amt))} disabled={!!busy} className="px-5 py-2 font-black uppercase border-2 rounded-full transition-colors duration-200 hover:bg-white/10" style={{ borderColor: ORANGE, color: ORANGE }} data-testid="balance-plan-pay-btn">Pay this amount</button>
              </div>
            </Option>
            <Option icon={DoorOpen} title="3 · Withdraw" testid="balance-option-withdraw">
              <p className="text-sm text-gray-300 mb-3">End enrollment. {b.student_name.split(' ')[0]}'s SAA account closes right away and no new tuition is added.</p>
              {!confirmW ? (
                <button onClick={() => setConfirmW(true)} className="px-5 py-2 font-bold uppercase text-sm border border-white/30 rounded-full hover:bg-white/10 transition-colors duration-200" data-testid="balance-withdraw-btn">Choose withdrawal</button>
              ) : (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-gray-400">Are you sure? This closes the account now.</span>
                  <button onClick={withdraw} disabled={!!busy} className="px-4 py-2 font-bold uppercase text-sm bg-red-600 rounded-full" data-testid="balance-withdraw-confirm-btn">{busy === 'withdraw' ? 'Saving…' : 'Yes, withdraw'}</button>
                  <button onClick={() => setConfirmW(false)} className="px-4 py-2 text-sm text-gray-400" data-testid="balance-withdraw-cancel-btn">Cancel</button>
                </div>
              )}
            </Option>
          </div>
        )}
        <p className="text-[11px] text-gray-500 mt-8">Questions? Reply to our email or write to info@prsvr.com.</p>
      </div>
    </div>
  );
}
